import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./referralPhysicians.css";
import {
  OTHER_SPECIALTY, emptyForm, errorBody, formatPhone, fromPhysician, physicianSubline,
  toBody, validateForm, withTitle,
} from "./physicianForm";
import { createPhysician, updatePhysician } from "./referralPhysiciansApi";

// Add (anyone with the Order privilege) or Edit (admins) one referral physician
// (owner item 3, 2026-10-19). Display name + specialty are required; NPI,
// phone, fax, clinic and address are optional. The display name is what the
// referral order carries ("Dr. Patel-<diagnosis>"); a hyphen in it is stored
// as a look-alike hyphen by the server so the order still splits right.
//
// Server answers handled here: 400 INVALID (field message), 409 DUPLICATE
// (offer the existing physician), 409 POSSIBLE_DUPLICATE (candidates + "Add
// anyway"), 409 RENAME_IN_USE / other (banner).

const Field = ({ id, label, required, error, wide, children }) => (
  <div className={`rp-field${error ? " bad" : ""}${wide ? " rp-wide" : ""}`}>
    <label htmlFor={id}>
      {label}
      {required && <span className="rp-req" aria-hidden="true">*</span>}
    </label>
    {children}
    {error && <p className="rp-err" role="alert">{error}</p>}
  </div>
);

const hasAddress = (f) => !!(f.practice_name || f.address1 || f.address2 || f.city || f.state || f.zip);

export default function AddPhysicianDialog({
  open, onClose, mode = "add", initialName = "", physician = null, access = {}, onSaved, onUseExisting,
}) {
  const specialties = Array.isArray(access.specialties) ? access.specialties : [];
  const credentials = Array.isArray(access.credentials) ? access.credentials : [];
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => emptyForm(initialName));
  const [nameTouched, setNameTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [addFavourite, setAddFavourite] = useState(true);

  useEffect(() => {
    if (!open) return;
    const f = isEdit && physician ? fromPhysician(physician, specialties) : emptyForm(initialName);
    setForm(f);
    setNameTouched(isEdit || !!String(initialName || "").trim());
    setErrors({});
    setBanner(null);
    setSaving(false);
    setShowMore(isEdit && hasAddress(f));
    setAddFavourite(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, physician, initialName]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // The display name follows the last name until the user edits it.
      if (key === "last_name" && !nameTouched) next.doctor_name = withTitle(value);
      return next;
    });
    if (key === "doctor_name") setNameTouched(true);
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const save = async (confirmDuplicate = false) => {
    const errs = validateForm(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    setBanner(null);
    try {
      const payload = { ...toBody(form), confirm_duplicate: !!confirmDuplicate };
      const res = isEdit ? await updatePhysician(physician.id, payload) : await createPhysician(payload);
      const row = (res && res.data) || null;
      if (onSaved) onSaved(row, { addFavourite: !isEdit && addFavourite });
    } catch (err) {
      const b = errorBody(err) || {};
      if (b.code === "INVALID" && b.field) {
        setErrors({ [b.field]: b.message });
        if (["practice_name", "address1", "address2", "city", "state", "zip"].includes(b.field)) setShowMore(true);
      } else if (b.code === "DUPLICATE") {
        setBanner({ kind: "dup", message: b.message, existing: b.existing || null });
      } else if (b.code === "POSSIBLE_DUPLICATE") {
        setBanner({ kind: "possible", message: b.message, candidates: Array.isArray(b.candidates) ? b.candidates : [] });
      } else {
        setBanner({ kind: "error", message: b.message || "Could not save. Please try again." });
      }
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!saving) save(false);
  };

  const pickExisting = (row) => {
    if (onUseExisting && row) onUseExisting(row);
  };

  return (
    <Dialog open={!!open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ style: { borderRadius: 16 } }} aria-labelledby="rp-dialog-title">
      <DialogContent>
        <form className="rp" onSubmit={onSubmit} noValidate>
          <div className="rp-head">
            <div>
              <h2 id="rp-dialog-title" className="rp-title">{isEdit ? "Edit physician" : "Add physician"}</h2>
              <p className="rp-sub">
                {isEdit ? "Changes apply to new referrals." : "Saved to your practice's referral list."}
              </p>
            </div>
            <IconButton onClick={onClose} disabled={saving} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </div>

          <div className="rp-grid">
            <Field id="rp-first" label="First name">
              <input id="rp-first" value={form.first_name} onChange={set("first_name")} autoComplete="off" maxLength={60} />
            </Field>
            <Field id="rp-last" label="Last name">
              <input id="rp-last" value={form.last_name} onChange={set("last_name")} autoComplete="off" maxLength={60} />
            </Field>
            <Field id="rp-name" label="Display name" required error={errors.doctor_name} wide>
              <input id="rp-name" value={form.doctor_name} onChange={set("doctor_name")} autoComplete="off"
                     maxLength={60} placeholder="Dr. Patel" />
            </Field>
            <Field id="rp-spec" label="Specialty" required error={errors.specialty}>
              <select id="rp-spec" value={form.specialty} onChange={set("specialty")}>
                <option value="">Choose…</option>
                {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
                <option value={OTHER_SPECIALTY}>Other…</option>
              </select>
            </Field>
            {form.specialty === OTHER_SPECIALTY ? (
              <Field id="rp-spec-other" label="Specialty (other)" required error={errors.specialty}>
                <input id="rp-spec-other" value={form.specialtyOther} onChange={set("specialtyOther")} maxLength={60} />
              </Field>
            ) : (
              <Field id="rp-cred" label="Credentials">
                <select id="rp-cred" value={form.credentials} onChange={set("credentials")}>
                  <option value="">—</option>
                  {credentials.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            )}
            <Field id="rp-npi" label="NPI" error={errors.npi}>
              <input id="rp-npi" value={form.npi} onChange={set("npi")} inputMode="numeric" maxLength={12}
                     placeholder="10 digits" />
            </Field>
            <Field id="rp-phone" label="Phone" error={errors.phone}>
              <input id="rp-phone" value={form.phone} onChange={set("phone")} inputMode="tel" maxLength={20}
                     onBlur={() => setForm((p) => ({ ...p, phone: formatPhone(p.phone) }))} />
            </Field>
            <Field id="rp-fax" label="Fax" error={errors.fax}>
              <input id="rp-fax" value={form.fax} onChange={set("fax")} inputMode="tel" maxLength={20}
                     onBlur={() => setForm((p) => ({ ...p, fax: formatPhone(p.fax) }))} />
            </Field>
            {showMore && (
              <>
                <Field id="rp-clinic" label="Clinic / practice" error={errors.practice_name} wide>
                  <input id="rp-clinic" value={form.practice_name} onChange={set("practice_name")} maxLength={150} />
                </Field>
                <Field id="rp-addr1" label="Address" error={errors.address1} wide>
                  <input id="rp-addr1" value={form.address1} onChange={set("address1")} maxLength={150} />
                </Field>
                <Field id="rp-addr2" label="Address line 2" error={errors.address2} wide>
                  <input id="rp-addr2" value={form.address2} onChange={set("address2")} maxLength={150} />
                </Field>
                <Field id="rp-city" label="City" error={errors.city}>
                  <input id="rp-city" value={form.city} onChange={set("city")} maxLength={80} />
                </Field>
                <Field id="rp-state" label="State" error={errors.state}>
                  <input id="rp-state" value={form.state} onChange={set("state")} maxLength={2} placeholder="TX" />
                </Field>
                <Field id="rp-zip" label="ZIP" error={errors.zip}>
                  <input id="rp-zip" value={form.zip} onChange={set("zip")} inputMode="numeric" maxLength={10} />
                </Field>
              </>
            )}
          </div>
          {!showMore && (
            <button type="button" className="rp-more" onClick={() => setShowMore(true)}>
              + Clinic and address
            </button>
          )}

          {!isEdit && (
            <label className="rp-check">
              <input type="checkbox" checked={addFavourite} onChange={(e) => setAddFavourite(e.target.checked)} />
              Add to my favourites
            </label>
          )}

          {banner && banner.kind === "dup" && (
            <div className="rp-banner" role="status">
              {banner.message}
              {banner.existing && banner.existing.active && onUseExisting && (
                <ul>
                  <li>
                    <span>
                      <strong>{banner.existing.doctor_name}</strong>
                      <span className="rp-result-sub">{physicianSubline(banner.existing)}</span>
                    </span>
                    <button type="button" className="rp-btn small" onClick={() => pickExisting(banner.existing)}>
                      Use this one
                    </button>
                  </li>
                </ul>
              )}
            </div>
          )}
          {banner && banner.kind === "possible" && (
            <div className="rp-banner" role="status">
              {banner.message}
              <ul>
                {banner.candidates.map((c) => (
                  <li key={c.id}>
                    <span>
                      <strong>{c.doctor_name}</strong>
                      {!c.active && <span className="rp-pill">Inactive</span>}
                      <span className="rp-result-sub">{physicianSubline(c)}</span>
                    </span>
                    {c.active && onUseExisting && (
                      <button type="button" className="rp-btn small" onClick={() => pickExisting(c)}>Use</button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {banner && banner.kind === "error" && <div className="rp-banner err" role="alert">{banner.message}</div>}

          <div className="rp-actions">
            <button type="button" className="rp-btn" onClick={onClose} disabled={saving}>Cancel</button>
            {banner && banner.kind === "possible" && (
              <button type="button" className="rp-btn" onClick={() => save(true)} disabled={saving}>
                {isEdit ? "Save anyway" : "Add anyway"}
              </button>
            )}
            <button type="submit" className="rp-btn primary" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save" : "Add physician"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
